/**
 * useDictsAdmin — данные и мутации «EO → Конфигуратор → Справочники»
 * (screen-21, A44/DICT-2). Шаблон — useServicesAdmin: allSettled-загрузка с
 * ok-картой скоупов, optimistic-слои phantom/gone/over, run с rollback и
 * hint (404 → «уже удалено», сеть → откат), создание с client-id (один на
 * сессию формы — идемпотентный повтор «Создать»). Удаление — Д6: K=0 —
 * deferred DELETE c keepalive (undo-снекбар хоста), K>0 — немедленный
 * DELETE без force; 409 сервера {uses,count} поднимает guard-стейт для
 * CfgModal danger, «Всё равно удалить» — повтор с ?force=1. Карты привязок
 * (bindings-map + quest-resources) — под фильтры Д9. Карточка позиции —
 * отдельный ленивый useDictCard. app.reload() в мутациях НЕ дёргаем:
 * списки — собственный fetch, справочники useAppData обновляет PTR.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { ApiException, type ApiList, type ApiOk, type QuestResource } from "../api/types";
import type { DictBindingsMap, DictConsumable, DictInUse, DictResource } from "../api/dictTypes";
import type { ConsLinks } from "./dictFilters";
import { dictErr } from "../screens/eo/dictErr";

export type DictKind = "c" | "r";

/** Данные guard-модалки «Используется в K местах» (409 сервера, Д6). */
export interface DictGuard {
  kind: DictKind;
  id: string;
  name: string;
  uses: string[];
  count: number;
}

interface Fetched {
  cons: DictConsumable[];
  ress: DictResource[];
  consLinks: ConsLinks | null;
  resLinks: Record<string, string[]> | null;
  loaded: boolean;
  ok: { c: boolean; r: boolean; map: boolean; qr: boolean } | null;
}
const F0: Fetched = { cons: [], ress: [], consLinks: null, resLinks: null, loaded: false, ok: null };

const got = <T,>(r: PromiseSettledResult<ApiList<T>>): T[] | null =>
  (r.status === "fulfilled" ? r.value.results ?? [] : null);
const byName = <T extends { name: string }>(rows: T[]): T[] =>
  [...rows].sort((a, b) => a.name.localeCompare(b.name, "ru"));
const push = (o: Record<string, string[]>, k: string, v: string) => { (o[k] ??= []).push(v); };

export interface DictsAdmin {
  cons: DictConsumable[];
  ress: DictResource[];
  consLinks: ConsLinks | null;
  resLinks: Record<string, string[]> | null;
  loaded: boolean; loadErr: boolean; hint: string | null;
  /** flashOk строки `c:{id}` / `r:{id}` + счётчик n (ремаунт анимации). */
  flash: { key: string; n: number } | null;
  /** Флэш строки извне (DICT-3: «Сохранить» карточки). */
  flashNow: (key: string) => void;
  /** Optimistic-патч строки списка (DICT-3: сейв полей карточки). */
  patchRow: (kind: DictKind, id: string, patch: Partial<DictConsumable & DictResource>) => void;
  /** id — client-id формы (один на сессию, идемпотентный повтор). */
  createCons: (id: string, name: string, unit: string) => Promise<string | null>;
  createRes: (id: string, name: string) => Promise<string | null>;
  hide: (id: string) => void;
  unhide: (id: string) => void;
  /** Deferred DELETE (onExpire снекбара, строка уже спрятана) — keepalive. */
  removeNow: (kind: DictKind, id: string, name: string) => void;
  /** K>0: немедленный DELETE без force — 409 сервера поднимет guard. */
  tryDelete: (kind: DictKind, id: string, name: string) => void;
  /** «Всё равно удалить» из guard-модалки: DELETE ?force=1. */
  forceDelete: (g: DictGuard) => void;
  guard: DictGuard | null;
  clearGuard: () => void;
  refetch: () => void;
}

export function useDictsAdmin(): DictsAdmin {
  const [data, setData] = useState<Fetched>(F0);
  const [overC, setOverC] = useState<Record<string, Partial<DictConsumable>>>({});
  const [overR, setOverR] = useState<Record<string, Partial<DictResource>>>({});
  const [phantomC, setPhantomC] = useState<DictConsumable[]>([]);
  const [phantomR, setPhantomR] = useState<DictResource[]>([]);
  const [gone, setGone] = useState<string[]>([]);
  const [guard, setGuard] = useState<DictGuard | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ key: string; n: number } | null>(null);
  const [tick, setTick] = useState(0);
  const pendingRef = useRef(0);
  const flashT = useRef(0);

  useEffect(() => {
    let dead = false;
    void Promise.allSettled([
      api<ApiList<DictConsumable>>("/api/consumables"),
      api<ApiList<DictResource>>("/api/resources"),
      api<DictBindingsMap>("/api/consumables/bindings-map"),
      api<ApiList<QuestResource>>("/api/quest-resources"),
    ]).then(([rc, rr, rm, rq]) => {
      if (dead) return;
      const cons = got(rc);
      const ress = got(rr);
      const map = rm.status === "fulfilled" ? rm.value : null;
      const qr = got(rq);
      setData((old) => {
        let consLinks = old.consLinks;
        if (map) {
          consLinks = { quests: {}, teas: {}, branches: {} };
          for (const b of map.quests) push(consLinks.quests, b.consumable_id, b.quest_id);
          for (const b of map.teas) push(consLinks.teas, b.consumable_id, b.tea_room_id);
          for (const b of map.branches ?? []) push(consLinks.branches!, b.consumable_id, b.branch_id);
        }
        let resLinks = old.resLinks;
        if (qr) {
          resLinks = {};
          for (const b of qr) push(resLinks, b.resource_id, b.quest_id);
        }
        return {
          cons: cons ? byName(cons) : old.cons,
          ress: ress ? byName(ress) : old.ress,
          consLinks, resLinks,
          loaded: old.loaded || !!(cons || ress),
          /* ok — исход ПОСЛЕДНЕГО фетча (не липкий): упавший refetch/PTR
             честно взводит loadErr-hint, старые данные при этом остаются. */
          ok: { c: !!cons, r: !!ress, map: !!map, qr: !!qr },
        };
      });
      /* Полная свежая правда и мутаций в полёте нет — оверлеи не нужны. */
      if (pendingRef.current === 0 && cons && ress && map && qr) { setOverC({}); setOverR({}); }
    });
    return () => { dead = true; };
  }, [tick]);
  useEffect(() => () => window.clearTimeout(flashT.current), []);

  const doFlash = (key: string) => {
    setFlash((f) => ({ key, n: (f?.n ?? 0) + 1 }));
    window.clearTimeout(flashT.current);
    flashT.current = window.setTimeout(() => setFlash(null), 900);
  };

  /** Обёртка мутации (useServicesAdmin): flash/rollback+hint, refetch в finally. */
  const run = (fn: () => Promise<string | null>, rollback: () => void): Promise<boolean> => {
    setHint(null);
    pendingRef.current += 1;
    return fn()
      .then((key) => { if (key) doFlash(key); return true; })
      .catch((err: unknown) => {
        rollback();
        /* 05.10: причина — с сервера (dictErr); «сеть» — только когда ответа не было */
        setHint(err instanceof ApiException && err.status === 404
          ? "Не сохранилось: уже удалено другим пользователем. Список обновлён."
          : `Не сохранилось: ${dictErr(err)}. Значение возвращено.`);
        return false;
      })
      .finally(() => { pendingRef.current -= 1; setTick((n) => n + 1); });
  };

  const cons = byName(data.cons.concat(phantomC.filter((p) => !data.cons.some((x) => x.id === p.id))))
    .filter((x) => !gone.includes(x.id))
    .map((x) => (overC[x.id] ? { ...x, ...overC[x.id] } : x));
  const ress = byName(data.ress.concat(phantomR.filter((p) => !data.ress.some((x) => x.id === p.id))))
    .filter((x) => !gone.includes(x.id))
    .map((x) => (overR[x.id] ? { ...x, ...overR[x.id] } : x));

  const createCons = (id: string, name: string, unit: string): Promise<string | null> => {
    /* use_count нового = 0 локально: списка-ответа с ним у POST нет (DICT-1). */
    setPhantomC((p) => [...p.filter((x) => x.id !== id), { id, name, unit, cost: 0, stock: 0, active: 1, use_count: 0 }]);
    return run(async () => {
      await api<ApiOk>("/api/consumables", { method: "POST", body: { id, name, unit } });
      return `c:${id}`;
    }, () => setPhantomC((p) => p.filter((x) => x.id !== id))).then((ok) => (ok ? id : null));
  };

  const createRes = (id: string, name: string): Promise<string | null> => {
    setPhantomR((p) => [...p.filter((x) => x.id !== id), { id, name, cost: 0, active: 1, branch_id: null, use_count: 0 }]);
    return run(async () => {
      /* type не шлём — сервер пишет сентинел 'item' (легаси-CHECK таблицы). */
      await api<ApiOk>("/api/resources", { method: "POST", body: { id, name } });
      return `r:${id}`;
    }, () => setPhantomR((p) => p.filter((x) => x.id !== id))).then((ok) => (ok ? id : null));
  };

  const hide = (id: string) => setGone((g) => (g.includes(id) ? g : [...g, id]));
  const unhide = (id: string) => setGone((g) => g.filter((x) => x !== id));

  /** Общий DELETE трёх сценариев Д6; keepalive — deferred-вызов обязан
   *  пережить выгрузку страницы (контракт UndoSnackbar). notify — фидбэк
   *  успеха для немедленных путей (K>0): force-удаление и стейл-счётчик
   *  (сервер увидел K=0 и удалил без снекбара) иначе завершались бы тишиной
   *  (эталон J:118 после kill показывает «…удалён»). */
  const doDelete = (kind: DictKind, id: string, name: string, force: boolean, notify?: boolean) => {
    setHint(null);
    pendingRef.current += 1;
    api<ApiOk>(
      `/api/${kind === "c" ? "consumables" : "resources"}/${encodeURIComponent(id)}${force ? "?force=1" : ""}`,
      { method: "DELETE", keepalive: true },
    )
      .then(() => {
        hide(id);
        if (notify) setHint(`${kind === "c" ? "Расходник" : "Ресурс"} «${name}» удалён.`);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiException && err.status === 409) {
          const b = err.body as unknown as Partial<DictInUse> | null;
          unhide(id);
          setGuard({ kind, id, name, uses: b?.uses ?? [], count: b?.count ?? b?.uses?.length ?? 0 });
        } else if (err instanceof ApiException && err.status === 404) {
          /* unhide: gone не должен прятать строку навечно — refetch в finally
             сам уберёт её из data, если её реально нет; если есть (гонка
             deferred-DELETE с ретраящимся POST создания) — честно вернётся. */
          unhide(id);
          setHint("Уже удалено другим пользователем. Список обновлён.");
        } else {
          unhide(id);
          setHint(`Не удалилось: ${dictErr(err)}. Строка возвращена.`);
        }
      })
      .finally(() => { pendingRef.current -= 1; setTick((n) => n + 1); });
  };

  const forceDelete = (g: DictGuard) => { setGuard(null); hide(g.id); doDelete(g.kind, g.id, g.name, true, true); };

  const patchRow = (kind: DictKind, id: string, patch: Partial<DictConsumable & DictResource>) => {
    if (kind === "c") setOverC((o) => ({ ...o, [id]: { ...o[id], ...patch } }));
    else setOverR((o) => ({ ...o, [id]: { ...o[id], ...patch } }));
  };

  return {
    cons, ress, consLinks: data.consLinks, resLinks: data.resLinks,
    loaded: data.loaded,
    loadErr: data.ok != null && !(data.ok.c && data.ok.r && data.ok.map && data.ok.qr),
    hint, flash, flashNow: doFlash, patchRow, createCons, createRes,
    hide, unhide,
    removeNow: (kind, id, name) => doDelete(kind, id, name, false),
    tryDelete: (kind, id, name) => doDelete(kind, id, name, false, true),
    forceDelete, guard, clearGuard: () => setGuard(null),
    refetch: () => setTick((n) => n + 1),
  };
}
